import * as THREE from 'three';
import bodyModelUrl from './body.drc?url';
import { DecodeDracoUrlToBufferGeometry } from '@/engine/Utilities/DracoUtils';
import type { ICubePresentationBuilder } from './interfaces';
import { injectable } from 'inversify';

@injectable()
export class CubePresentationBuilder implements ICubePresentationBuilder {
  private readonly _cubeSize = 100;

  private readonly _bodyMeshColor: THREE.Color = new THREE.Color(0xaaaaaa);

  private _faceMaterial?: THREE.Material;

  private readonly _bodyModelMaterial: THREE.Material;

  private readonly _zVector: THREE.Vector3;

  public constructor() {
    this._bodyModelMaterial = new THREE.MeshLambertMaterial({
      color: this._bodyMeshColor
    });

    this._zVector = new THREE.Vector3(0, 0, 1);
  }

  public getBoundingSphere(): THREE.Sphere {
    return new THREE.Sphere(new THREE.Vector3(0, 0, 0), (this._cubeSize * Math.sqrt(3)) / 2);
  }

  private _createFace(normal: THREE.Vector3, top: THREE.Vector3, name: string): THREE.Mesh {
    const geometry = new THREE.PlaneGeometry(this._cubeSize, this._cubeSize, 30, 30);

    const mesh = new THREE.Mesh(geometry, this._faceMaterial);
    mesh.matrixAutoUpdate = false;
    mesh.name = name;

    const cs = new THREE.Matrix4()
      .makeRotationFromQuaternion(new THREE.Quaternion().setFromUnitVectors(this._zVector, normal))
      .setPosition(new THREE.Vector3().copy(normal).multiplyScalar(this._cubeSize / 2));

    const y = new THREE.Vector3(0, 1, 0);
    y.transformDirection(cs);

    const axis = normal.clone();
    if (axis.angleTo(top.clone().cross(y)) < Math.PI / 2) axis.negate();

    const rotation = new THREE.Matrix4().makeRotationFromQuaternion(
      new THREE.Quaternion().setFromAxisAngle(axis, top.angleTo(y))
    );

    mesh.matrix.copy(cs.premultiply(rotation));
    mesh.updateMatrixWorld(true);

    return mesh;
  }

  public buildFaces(material: THREE.Material): THREE.Object3D {
    this._faceMaterial = material;
    const faces = new THREE.Object3D();
    faces.add(this._createFace(new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 0, 1), 'right'));
    faces.add(this._createFace(new THREE.Vector3(-1, 0, 0), new THREE.Vector3(0, 0, 1), 'left'));
    faces.add(this._createFace(new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 1), 'back'));
    faces.add(this._createFace(new THREE.Vector3(0, -1, 0), new THREE.Vector3(0, 0, 1), 'front'));
    faces.add(this._createFace(new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0), 'top'));
    faces.add(this._createFace(new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, -1, 0), 'bottom'));
    return faces;
  }

  public buildEdges(material: THREE.Material): THREE.Object3D {
    const geometry = new THREE.BoxGeometry(this._cubeSize, this._cubeSize, this._cubeSize);

    const edges = new THREE.EdgesGeometry(geometry);

    return new THREE.LineSegments(edges, material);
  }

  public buildInnerModel(onReady: () => void): THREE.Object3D {
    const mesh = new THREE.Mesh(
      new THREE.ConeGeometry(this._cubeSize / 7, this._cubeSize).rotateX(Math.PI / 2),
      this._bodyModelMaterial
    );
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    CubePresentationBuilder._loadInnerModelGeometryAsync(mesh, this._cubeSize, onReady);
    return mesh;
  }

  private static async _loadInnerModelGeometryAsync(
    mesh: THREE.Mesh,
    cubeSize: number,
    onReady: () => void
  ): Promise<void> {
    const bodyGeometry = await DecodeDracoUrlToBufferGeometry(bodyModelUrl);

    bodyGeometry.computeBoundingSphere();
    if (!bodyGeometry.boundingSphere) {
      throw new Error('BoundingSphere generation failed');
    }

    const { radius, center } = bodyGeometry.boundingSphere;
    const scaleFactor = cubeSize / radius / 2;

    const matrix = new THREE.Matrix4().makeScale(scaleFactor, scaleFactor, scaleFactor);

    center.applyMatrix4(matrix);
    matrix.premultiply(new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z));

    bodyGeometry.applyMatrix4(matrix);

    mesh.geometry = bodyGeometry;

    onReady();
  }
}
